import { forEach } from 'lodash';
import { apiFunctionsEnum } from 'constants/api.constants';
import ApiService from './api.service';
import { setError, setLoading, setPendingRequestsAnalytics } from 'state/reducers/pending-requests.reducer';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { buildPaginationQueryParams, IPaginationParams, setErrorMessage } from 'utils/index';
import FormData from 'form-data';
import { BalanceStatus } from 'constants/balance.constants';
import { startOfDay, endOfDay } from 'date-fns';
import { pendingRequestsTypes } from 'constants/requests.constants';
import type { DuplicateBalanceCheckResult } from 'constants/pending-requests.constants';

export function getAllAnalyticsPendingRequests() {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		await ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_ANALYTICS_PENDING_REQUESTS, {})
			.then((response: any) => {
				dispatch(setPendingRequestsAnalytics(response.data));
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			})
			.finally(() => {
				dispatch(setLoading(false));
			});
	};
}

export function getAvailablePaymentDates(currencyId: string, paymentDate: string) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));

		try {
			const response = await ApiService.getInstance().execute(apiFunctionsEnum.AVAILABLE_PAYMENT_DAYS, {
				currencyId,
				paymentDate,
			});

			dispatch(setLoading(false));
			return response;
		} catch (error: any) {
			dispatch(setLoading(false));
			setErrorMessage(setError, {
				errorNumber: error?.response?.data?.error_number,
				statusCode: error?.response?.status,
			});
			throw error;
		}
	};
}

export const getFeature = createAsyncThunk(
	'pendingRequests/getFeature',
	(props: { name: string; data: Record<string, any> }, { rejectWithValue }) =>
		ApiService.getInstance()
			.execute(props.name, props.data)
			.then((response) => {
				return response.data;
			})
			.catch((error) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
				rejectWithValue(error);
			})
);

export const approveFeature = createAsyncThunk(
	'pendingRequests/approveFeature',
	(props: { name: string; data: Record<string, any> }, { rejectWithValue }) => {
		let data;
		if (props.data.file) {
			const formData = new FormData();
			const { file, ...dataWithoutFile } = props.data;
			formData.append('file', file, file.name);
			forEach(Object.keys(dataWithoutFile), (key) => {
				formData.append(key, dataWithoutFile[key]);
			});
			data = formData;
		} else {
			data = props.data;
		}
		return ApiService.getInstance()
			.execute(props.name, data)
			.then((response) => {
				return response.data;
			})
			.catch((error) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
				return rejectWithValue(error);
			});
	}
);

export const declineFeature = createAsyncThunk(
	'pendingRequests/declineFeature',
	(props: { name: string; data: Record<string, any> }, { rejectWithValue }) =>
		ApiService.getInstance()
			.execute(props.name, props.data)
			.then((response) => {
				return response.data;
			})
			.catch((err) => {
				setErrorMessage(setError, {
					errorNumber: err?.response?.data?.error_number,
					statusCode: err?.response?.status,
				});
				return rejectWithValue(err);
			})
);

export const createFeature = createAsyncThunk(
	'pendingRequests/createFeature',
	(props: { name: string; data: Record<string, any> }, { rejectWithValue }) => {
		let data;
		let additionalHeaders = {};
		if (props.data.file) {
			const formData = new FormData();
			const { file, ...dataWithoutFile } = props.data;
			formData.append('file', file, file.name);
			forEach(Object.keys(dataWithoutFile), (key) => {
				formData.append(key, dataWithoutFile[key]);
			});
			data = formData;
			additionalHeaders = { 'Content-Type': 'multipart/form-data' };
		} else {
			data = props.data;
		}

		return ApiService.getInstance()
			.execute(props.name, data, { ...additionalHeaders })
			.then((response) => {
				return response.data;
			})
			.catch((err) => {
				setErrorMessage(setError, {
					errorNumber: err?.response?.data?.error_number,
					statusCode: err?.response?.status,
				});
				rejectWithValue(err);
				return err;
			});
	}
);
export const getBoSignerAllBalanceRequests = createAsyncThunk(
	'pendingRequests/balanceRequests',
	(_, { rejectWithValue }) =>
		ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_BO_SIGNER_ALL_BALANCE_REQUESTS)
			.then((response) => {
				return response.data;
			})
			.catch((err) => rejectWithValue(err))
);

export const getBoSignerBalanceRequestsByStatus = createAsyncThunk(
	'pendingRequests/boSignerBalanceRequestsByStatus',
	({ status, ...params }: { status: BalanceStatus } & IPaginationParams, { rejectWithValue }) => {
		const queryParams = buildPaginationQueryParams(params);
		return ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_BO_SIGNER_BALANCE_REQUESTS_BY_STATUS, {
				status,
				...queryParams,
			})
			.then((response) => {
				return { status, data: response.data };
			})
			.catch((err) => rejectWithValue(err));
	}
);
export const getSupportRequestById = createAsyncThunk('fetchData', (id: number, { rejectWithValue }) =>
	ApiService.getInstance()
		.execute(apiFunctionsEnum.GET_SUPPORT_REQUEST, { id })
		.then((response) => {
			return response.data;
		})
		.catch((err) => rejectWithValue(err))
);

export const withdrawalWaitOnHouseAccountApproval = createAsyncThunk(
	'fetchData',
	(props: { balanceActivityId: number; isNotRequired?: boolean }, { rejectWithValue }) => {
		const { balanceActivityId, isNotRequired } = props;
		ApiService.getInstance()
			.execute(apiFunctionsEnum.WITHDRAWL_WAIT_HOUSE_ACCOUNT_APPROVAL, { id: balanceActivityId, isNotRequired })
			.then((response) => {
				return response.data;
			})
			.catch((err) => console.error(err));
	}
);

export const transferDetailsHouseToAltshulerAccount = createAsyncThunk(
	'fetchData',
	(
		props: { balanceRequestId: number; currencyId: number; amount: number; altshulerAccountId: number },
		{ rejectWithValue }
	) => {
		const { balanceRequestId, currencyId, amount, altshulerAccountId } = props;
		ApiService.getInstance()
			.execute(apiFunctionsEnum.TRANSFER_DETAILS_HOUSE_TO_ALTSHULER_ACCOUNT, {
				balanceRequestId,
				currencyId,
				amount,
				altshulerAccountId,
			})
			.then((response) => {
				return response.data;
			})
			.catch((err) => console.error(err));
	}
);

export const transferDetailsFinalCustomerBankAccount = createAsyncThunk(
	'fetchData',
	(
		props: {
			balanceRequestId: number;
			currencyId: number;
			amount: number;
			altshulerAccountId: number;
			customerBankAccountId: number;
			reference: string;
			file: any;
		},
		{ rejectWithValue }
	) => {
		const { balanceRequestId, currencyId, amount, altshulerAccountId, customerBankAccountId, reference, file } =
			props;
		const formData = new FormData();
		formData.append('balanceRequestId', balanceRequestId);
		formData.append('currencyId', currencyId);
		formData.append('amount', amount);
		formData.append('altshulerAccountId', altshulerAccountId);
		formData.append('customerBankAccountId', customerBankAccountId);
		formData.append('reference', reference);
		formData.append('file', file);

		return ApiService.getInstance()
			.execute(apiFunctionsEnum.TRANSFER_DETAILS_FINAL_CUSTOMER_BANK_ACCOUNT, formData, {
				'Content-Type': 'multipart/form-data',
			})
			.then((response) => {
				return response.data;
			})
			.catch((err) => console.error(err));
	}
);

export const createSupportWithdrawalRequest = createAsyncThunk(
	'fetchData',
	(
		props: {
			contactId: number;
			companyId: number;
			closeAccount: boolean;
		},
		{ rejectWithValue }
	) => {
		const { contactId, companyId, closeAccount } = props;

		return ApiService.getInstance()
			.execute(
				apiFunctionsEnum.CREATE_SUPPORT_WITHDRAWAL_REQUEST,
				{ contactId, companyId, closeAccount },
				{
					'Content-Type': 'multipart/form-data',
				}
			)
			.then((response) => {
				return response.data;
			})
			.catch((err) => console.error(err));
	}
);

export const updateDigitalBalanceDocumentedInHyper = createAsyncThunk(
	'pendingRequests/update_digital_balance_documented_in_hyper',
	(requestId: number, { rejectWithValue }) =>
		ApiService.getInstance()
			.execute(apiFunctionsEnum.UPDATE_DIGITAL_BALANCE_DOCUMENTED_IN_HYPER, {
				id: requestId,
				documentedOnHyper: true,
			})
			.then((response) => {
				return response.data;
			})
			.catch((error) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
				return rejectWithValue(error);
			})
);

export async function getDuplicateBalanceAdditionCompanies(
	companyId: number,
	createdAt: string
): Promise<DuplicateBalanceCheckResult> {
	const response = await ApiService.getInstance().execute(
		apiFunctionsEnum.GET_DUPLICATE_BALANCE_ADDITION_COMPANIES,
		{ companyId, createdAt }
	);
	const data = response?.data ?? response;
	return {
		hasDuplicates: data?.hasDuplicates ?? false,
		digitalBalanceAdditions: data?.digitalBalanceAdditions ?? [],
	};
}

// Map pending request type to API function
const typeToApiFunction: Partial<Record<pendingRequestsTypes, apiFunctionsEnum>> = {
	[pendingRequestsTypes.BANK_ACCOUNTS]: apiFunctionsEnum.GET_PENDING_BANK_ACCOUNTS,
	[pendingRequestsTypes.BALANCES]: apiFunctionsEnum.GET_PENDING_BALANCES,
	[pendingRequestsTypes.DIGITAL_BALANCES_REQUESTS]: apiFunctionsEnum.GET_COMPLETED_DIGITAL_BALANCE_REQUESTS,
	[pendingRequestsTypes.BENEFICIARIES]: apiFunctionsEnum.GET_PENDING_BENEFICIARIES,
	[pendingRequestsTypes.PAYMENTS]: apiFunctionsEnum.GET_PENDING_PAYMENTS,
	[pendingRequestsTypes.WITHDRAWAL_SUPPORT]: apiFunctionsEnum.GET_PENDING_WITHDRAWALS,
	[pendingRequestsTypes.WITHDRAWAL_BO_APPROVED]: apiFunctionsEnum.GET_PENDING_WITHDRAWALS_BO_APPROVED,
	[pendingRequestsTypes.BALANCES_BO_REQUESTS]: apiFunctionsEnum.GET_PENDING_BALANCES_BO,
};

// Build filters object (frontend calculates date ranges)
const buildFilters = (
	filterDate?: Date,
	companyId?: number,
	type?: pendingRequestsTypes
): Record<string, any> | undefined => {
	const filters: any = {};

	if (type === pendingRequestsTypes.DIGITAL_BALANCES_REQUESTS) {
		filters.documentedOnHyper = false;
	}

	if (filterDate) {
		filters.createdAt = {
			from: startOfDay(filterDate).toISOString(),
			to: endOfDay(filterDate).toISOString(),
		};
	}
	if (companyId !== undefined) {
		filters.companyId = companyId;
	}
	return Object.keys(filters).length > 0 ? filters : undefined;
};

// Generic thunk for all paginated pending request types
export const getPendingRequestsByType = createAsyncThunk(
	'pendingRequests/getPendingRequestsByType',
	(
		{
			type,
			filterDate,
			companyId,
			...params
		}: { type: pendingRequestsTypes; filterDate?: Date; companyId?: number } & IPaginationParams,
		{ rejectWithValue }
	) => {
		const filters = buildFilters(filterDate, companyId, type);
		const apiFunction = typeToApiFunction[type];

		if (!apiFunction) {
			return rejectWithValue(new Error(`Unknown pending request type: ${type}`));
		}

		const queryParams = buildPaginationQueryParams({ ...params, filters });

		return ApiService.getInstance()
			.execute(apiFunction, queryParams)
			.then((response) => ({ type, data: response.data }))
			.catch((error) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
				return rejectWithValue(error);
			});
	}
);
